/**
 * One primitive for the sidecar's HTTP surface.
 *
 * Every terminal pod runs a sidecar next to the gateway that speaks plain
 * HTTP: the model catalog (`/api/models`), session sync (`/sync/v1/*`) and
 * personalization (`/personalization/v1/*`). The pane-facing providers reach it
 * through `AgentActions.sidecarRequest`, which is this function wired to the
 * provider's base-URL and credential sources:
 *
 *   cloud (DIME) path:  `config.sidecar.baseUrl` — the host's web-api forward,
 *                       which injects the gateway credential server-side; the
 *                       headers are the host's (a DIME JWT).
 *   direct-gateway path: the HTTP origin derived from the ws URL, with the
 *                       stored gateway token (+ `X-Sync-User`).
 *
 * The fetch itself lives here, behind `getBase` / `getAuthHeaders`, so it can
 * be unit tested without a provider tree; `AgentProvider` only wires it.
 */

export type SidecarMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface SidecarRequestInit {
  /** Default `GET`. */
  readonly method?: SidecarMethod;
  /** JSON-encoded as the request body; `Content-Type` is set when present. */
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /**
   * What a 2xx body is. `json` (the default) parses it; `blob` hands back the
   * bytes untouched.
   *
   * `blob` exists for one route: a file the user attached to a chat, served
   * from the agent's own store. It cannot be an `<img src>` directly — the
   * route is authenticated and a browser sends no headers on an image load —
   * so the caller fetches it here and renders it through an object URL.
   */
  readonly expect?: 'json' | 'blob';
}

/**
 * Call the sidecar at `path` (absolute, e.g. `/personalization/v1/state`).
 * Resolves with the parsed JSON of a 2xx answer (`null` for an empty body);
 * rejects with a `SidecarError` for everything else, except an abort through
 * `signal`, which is rethrown as the `AbortError` fetch produced.
 */
export type SidecarRequest = (
  path: string,
  init?: SidecarRequestInit,
) => Promise<unknown>;

/**
 * Codes this module mints for failures that never reached the sidecar or
 * that the sidecar did not word. Upper-case, so they cannot collide with the
 * sidecar's own lower-case `{"error": "<code>"}` vocabulary.
 */
export const SIDECAR_CODES = {
  /** No HTTP base: no forward configured and nothing derivable from the ws URL. */
  NO_SIDECAR: 'NO_SIDECAR',
  /** The credential source returned nothing (not signed in yet, token not seeded). */
  NO_CREDENTIAL: 'NO_CREDENTIAL',
  /** `fetch` rejected: DNS, TLS, CORS, connection reset — nothing answered. */
  NETWORK: 'NETWORK',
  /** A 2xx whose body is not JSON. */
  BAD_RESPONSE: 'BAD_RESPONSE',
} as const;

export class SidecarError extends Error {
  /**
   * The HTTP status, or 0 when no HTTP exchange completed (no base, no
   * credential, network failure).
   */
  readonly status: number;
  /**
   * The sidecar's `{"error": "<code>"}` when the failure body carried one,
   * one of `SIDECAR_CODES` for client-side failures, else null (a non-JSON
   * error body, e.g. an ingress 502 page).
   */
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'SidecarError';
    this.status = status;
    this.code = code;
  }
}

/** Bounds a code echoed from a response body, which is untrusted input. */
const MAX_CODE_LENGTH = 64;

/** The `error` string of a JSON `{"error": "<code>"}` body, else null. */
function errorCodeOf(text: string): string | null {
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed === null || typeof parsed !== 'object') return null;
    const code = (parsed as { error?: unknown }).error;
    if (typeof code !== 'string' || code.length === 0) return null;
    return code.slice(0, MAX_CODE_LENGTH);
  } catch {
    return null;
  }
}

function isAbort(err: unknown): boolean {
  return (
    err !== null &&
    typeof err === 'object' &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}

function describe(err: unknown): string {
  if (err instanceof Error && err.message.length > 0) return err.message;
  return String(err);
}

export function createSidecarRequest(deps: {
  /** The HTTP origin (no trailing slash), or null when there is none. */
  readonly getBase: () => string | null;
  /** The auth headers for every call, or null when no credential exists yet. */
  readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
}): SidecarRequest {
  return async (path, init) => {
    const method = init?.method ?? 'GET';
    const base = deps.getBase();
    if (base == null) {
      throw new SidecarError(
        `${method} ${path}: no sidecar base URL for this gateway`,
        0,
        SIDECAR_CODES.NO_SIDECAR,
      );
    }
    const auth = deps.getAuthHeaders();
    if (auth == null) {
      throw new SidecarError(
        `${method} ${path}: no sidecar credential is available yet`,
        0,
        SIDECAR_CODES.NO_CREDENTIAL,
      );
    }
    const hasBody = init?.body !== undefined;
    const wantsBlob = init?.expect === 'blob';
    const headers: Record<string, string> = {
      Accept: wantsBlob ? '*/*' : 'application/json',
      ...auth,
    };
    if (hasBody) headers['Content-Type'] = 'application/json';
    const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

    let status: number;
    let ok: boolean;
    let text: string;
    try {
      // No `credentials: 'include'` — auth is the bearer header, mirroring the
      // sync client (and avoiding the wildcard-CORS credentials trap).
      const res = await fetch(url, {
        method,
        headers,
        body: hasBody ? JSON.stringify(init.body) : undefined,
        signal: init?.signal,
      });
      status = res.status;
      ok = res.ok;
      // A blob route's SUCCESS body is bytes, so it is never read as text; a
      // failure is, because the sidecar words those as `{"error": code}` like
      // every other route and the caller reads the code.
      if (wantsBlob && res.ok) {
        return await res.blob();
      }
      text = await res.text();
    } catch (err) {
      if (isAbort(err)) throw err;
      throw new SidecarError(
        `${method} ${path}: ${describe(err)}`,
        0,
        SIDECAR_CODES.NETWORK,
      );
    }

    if (!ok) {
      const code = errorCodeOf(text);
      throw new SidecarError(
        `${method} ${path} → ${status}${code === null ? '' : ` (${code})`}`,
        status,
        code,
      );
    }
    if (text.trim().length === 0) return null;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      throw new SidecarError(
        `${method} ${path} → ${status}: the answer is not JSON`,
        status,
        SIDECAR_CODES.BAD_RESPONSE,
      );
    }
  };
}
